import { usePaginatedQuery } from "convex/react";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface MatterCalendarProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

export function MatterCalendar({
	connectionId,
	clioMatterId,
}: MatterCalendarProps) {
	const pagination = usePaginatedQuery(
		api.clio.queries.listCalendarEntries,
		{ connectionId, clioMatterId },
		{ initialNumItems: 25 },
	);

	if (
		pagination.status === "LoadingFirstPage" &&
		pagination.results.length === 0
	) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading calendar…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (pagination.results.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No calendar entries.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{pagination.results.map((entry) => (
					<div key={entry._id} className="flex gap-3 p-4">
						<CalendarIcon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
						<div className="flex min-w-0 flex-1 flex-col gap-1">
							<div className="flex items-baseline gap-2">
								<div className="truncate text-sm font-medium">
									{entry.summary ?? "(no title)"}
								</div>
								<div className="ml-auto text-xs text-muted-foreground">
									{new Date(entry.startAt).toLocaleString()}
									{entry.endAt && entry.endAt !== entry.startAt && (
										<>
											{" – "}
											{new Date(entry.endAt).toLocaleTimeString([], {
												hour: "2-digit",
												minute: "2-digit",
											})}
										</>
									)}
								</div>
							</div>
							{entry.location && (
								<div className="text-xs text-muted-foreground">
									@ {entry.location}
								</div>
							)}
							{entry.description && (
								<div className="line-clamp-3 whitespace-pre-wrap text-sm text-foreground/80">
									{entry.description}
								</div>
							)}
							{entry.calendarOwnerName && (
								<div className="text-xs text-muted-foreground">
									{entry.calendarOwnerName}
								</div>
							)}
						</div>
					</div>
				))}
				{pagination.status === "CanLoadMore" && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => pagination.loadMore(25)}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
